import axios from 'axios';
import { getApiUrl, API_BASE_URL } from './utils/apiUtils';

/**
 * Small standalone module for directly saving breakdowns 
 * with exact structure as they appear in the calculation.
 */
class DirectBreakdownSave {
  /**
   * Save a breakdown directly from the calculation endpoint response
   * 
   * @param {Object} calculationData - The complete breakdown calculation response
   * @param {Object} params - Required parameters
   * @returns {Promise<Object>} - The saved breakdown
   */
  async saveDirectly(calculationData, { styleId, tariffId, propertyId, token = null }) {
    // Detailed parameter logging
    console.log('DirectSave.saveDirectly called with parameters:', { 
      styleId, 
      tariffId, 
      propertyId: typeof propertyId === 'object' ? 'object (error)' : propertyId,
      hasToken: !!token,
      validData: !!calculationData,
      hasCosts: calculationData?.costs ? true : false,
      hasBreakdown: calculationData?.breakdown ? true : false
    });
    
    if (!calculationData || !calculationData.costs || !calculationData.breakdown) {
      throw new Error('Invalid calculation data structure');
    }
    
    // More detailed validation
    if (!styleId) {
      throw new Error('Missing required parameter: styleId');
    }
    
    if (!tariffId) {
      throw new Error('Missing required parameter: tariffId');
    }
    
    if (!propertyId) {
      throw new Error('Missing required parameter: propertyId');
    }
    
    // Ensure propertyId is a string ID, not an object
    if (typeof propertyId === 'object') {
      throw new Error('PropertyId must be a string ID, not an object');
    }
    
    console.log('Saving breakdown with direct endpoint...');
    
    console.log('API Base URL:', API_BASE_URL);
    console.log('Costs:', {
      materialsCost: calculationData.costs.materialsCost,
      serviceCost: calculationData.costs.serviceCost,
      dependentMaterialsCost: calculationData.costs.dependentMaterialsCost || 0
    });
    console.log('Rooms count:', calculationData.breakdown.rooms?.length || 0);
    
    // Make sure dependentMaterialsCost is defined
    if (!calculationData.costs.dependentMaterialsCost) {
      calculationData.costs.dependentMaterialsCost = 0;
    }
    
    // Make sure dependentServiceCost is defined
    if (!calculationData.costs.dependentServiceCost) {
      calculationData.costs.dependentServiceCost = 0;
    }
    
    // Prepare request data
    const requestData = {
      styleId,
      tariffId,
      propertyId,
      calculatedResponse: calculationData
    };
    
    // Request headers
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Add auth token if provided
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    try {
      // Use the getApiUrl utility to get the proper URL
      const apiUrl = getApiUrl('repairs/direct-save-breakdown');
      console.log('Full API URL:', apiUrl);
      
      console.log('Making API request to:', apiUrl);
      console.log('Request headers:', headers);
      console.log('Request data summary:', {
        styleId: requestData.styleId,
        tariffId: requestData.tariffId,
        propertyId: requestData.propertyId,
        hasCosts: !!requestData.calculatedResponse?.costs,
        hasBreakdown: !!requestData.calculatedResponse?.breakdown,
        materialsCost: requestData.calculatedResponse?.costs?.materialsCost,
        roomsCount: requestData.calculatedResponse?.breakdown?.rooms?.length || 0
      });
      
      // Make the API request
      const response = await axios.post(
        apiUrl,
        requestData,
        { 
          headers,
          withCredentials: true,
          timeout: 30000 // 30 seconds timeout
        }
      );
      
      console.log('API response received:', {
        status: response.status,
        success: !!response.data?.success,
        hasData: !!response.data?.data,
        dataId: response.data?.data?._id
      });
      
      return response.data;
    } catch (error) {
      console.error('Error saving breakdown directly:', error);
      // Enhanced error logging
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Server error response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Request setup error:', error.message);
      }
      throw error;
    }
  }
  
  /**
   * Helper function to debug what's being saved
   */
  validateBreakdown(data) {
    console.log('Validating breakdown structure:');
    
    // Check costs
    const costs = data.costs || {};
    console.log('✅ Costs present:', !!costs);
    console.log('✅ Materials cost:', costs.materialsCost);
    console.log('✅ Dependent materials cost:', costs.dependentMaterialsCost);
    
    // Check rooms
    const rooms = data.breakdown?.rooms || [];
    console.log('✅ Rooms count:', rooms.length);
    
    if (rooms.length > 0) {
      const firstRoom = rooms[0];
      console.log('✅ Room has materials:', (firstRoom.materials?.length || 0) > 0);
      
      if ((firstRoom.materials?.length || 0) > 0) {
        const firstMaterial = firstRoom.materials[0];
        console.log('✅ Material has name:', !!firstMaterial.name);
        console.log('✅ Material has dependent materials:', 
          (firstMaterial.dependentMaterials?.length || 0) > 0);
      }
    }
    
    return true;
  }
}

// Export a singleton instance
export default new DirectBreakdownSave(); 